import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { Icon, type IconName } from "./icon";

// Folded sections are remembered per device, by record and section.
function readFolded(key: string): boolean {
	try {
		return localStorage.getItem(`live-fold:${key}`) === "1";
	} catch {
		return false;
	}
}

function writeFolded(key: string, folded: boolean) {
	try {
		if (folded) localStorage.setItem(`live-fold:${key}`, "1");
		else localStorage.removeItem(`live-fold:${key}`);
	} catch {
		// A device that refuses storage just forgets.
	}
}

// A section's fold state, remembered under `key`; no key, no folding.
export function useFolded(key: string | undefined): [boolean, () => void] {
	const [folded, setFolded] = useState(() => (key ? readFolded(key) : false));
	const toggle = () => {
		if (!key) return;
		writeFolded(key, !folded);
		setFolded(!folded);
	};
	return [folded, toggle];
}

// A titled section: an icon and a title in the serif title font. `fold` makes it fold, with a chevron at the right of
// the title; `key` is "<record>:<section>" and the labels name the chevron, which needs the section's own name.
export function Box({
	icon,
	title,
	titleClass = "",
	fold,
	class: className = "",
	actions,
	actionsTop = false,
	children,
}: {
	icon: IconName;
	title: ComponentChildren;
	titleClass?: string;
	fold?: { key: string; open: string; close: string };
	class?: string;
	// Stays on show while the box is folded, as the old page's section kept its actions in the title bar.
	actions?: ComponentChildren;
	// Puts `actions` right under the title bar instead of at the foot; still outside the fold.
	actionsTop?: boolean;
	children: ComponentChildren;
}) {
	const [folded, toggle] = useFolded(fold?.key);
	return (
		<section class={`cs-box flex flex-col ${className}`}>
			<div class="relative">
				<h2
					class={`cs-title flex flex-wrap items-center justify-center gap-x-1.5 pt-2 text-sm font-bold text-ink ${fold ? "px-9" : "px-2"} ${folded ? "pb-2" : "pb-0.5"} ${titleClass}`}
				>
					<Icon name={icon} class="h-3.5 w-3.5 text-ink-muted" />
					{title}
				</h2>
				{fold && (
					<button
						type="button"
						aria-expanded={!folded}
						aria-label={folded ? fold.open : fold.close}
						class="absolute right-1.5 top-1 inline-flex h-7 w-7 items-center justify-center rounded-lg text-ink-muted hover:bg-surface-sunken hover:text-ink"
						onClick={toggle}
					>
						<Icon name="chevron" class={`h-3 w-3 transition-transform ${folded ? "-rotate-90" : ""}`} />
					</button>
				)}
			</div>
			{actions && actionsTop && <div class="px-3 pb-2">{actions}</div>}
			<div hidden={folded} class="min-h-0 flex-1">
				{children}
			</div>
			{actions && !actionsTop && <div class="px-3 pb-3">{actions}</div>}
		</section>
	);
}
